import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import QRCode from 'react-qr-code';
import { 
  Sparkles, 
  Copy, 
  Check, 
  Search, 
  ExternalLink,
  RefreshCw,
  Link as LinkIcon,
  Zap,
  Smartphone,
  QrCode,
  Download,
  X
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function SmileDesignLinks({ adminToken }) {
  const [practices, setPractices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [copiedType, setCopiedType] = useState(null); // 'short' or 'long'
  const [generatingId, setGeneratingId] = useState(null);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const [qrModalPractice, setQrModalPractice] = useState(null); // Practice to show QR for
  const qrCodeRef = useRef(null);

  const baseUrl = window.location.origin;

  useEffect(() => {
    loadPractices();
  }, [adminToken]);

  const loadPractices = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/public/practices/all-slugs`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setPractices(data.data || []);
      }
    } catch (error) {
      console.error('Error loading practices:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateShortLink = async (practiceId) => {
    setGeneratingId(practiceId);
    try {
      const response = await fetch(`${API_BASE}/api/public/practices/generate-short-link`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          practiceId: practiceId,
          baseUrl: baseUrl
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Update the practice in state with the new short link
        setPractices(prev => prev.map(p => 
          p.id === practiceId 
            ? { ...p, shortLink: data.shortLink }
            : p
        ));
      } else {
        alert('Failed to generate short link: ' + (data.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error generating short link:', error);
      alert('Failed to generate short link. Please try again.');
    } finally {
      setGeneratingId(null);
    }
  };

  const generateAllShortLinks = async () => {
    if (!window.confirm('Generate short links for all practices without one? This may take a moment.')) {
      return;
    }
    
    setGeneratingAll(true);
    try {
      const response = await fetch(`${API_BASE}/api/public/practices/generate-all-short-links?base_url=${encodeURIComponent(baseUrl)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert(`Generated ${data.generated} short links. ${data.failed > 0 ? `Failed: ${data.failed}` : ''}`);
        loadPractices(); // Reload to get updated links
      } else {
        alert('Failed to generate short links');
      }
    } catch (error) {
      console.error('Error generating all short links:', error);
      alert('Failed to generate short links. Please try again.');
    } finally {
      setGeneratingAll(false);
    }
  };

  const copyToClipboard = async (text, id, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setCopiedType(type);
      setTimeout(() => {
        setCopiedId(null);
        setCopiedType(null);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(id);
      setCopiedType(type);
      setTimeout(() => {
        setCopiedId(null);
        setCopiedType(null);
      }, 2000);
    }
  };

  // Download QR Code as PNG
  const downloadQRCode = (practice) => {
    const svg = qrCodeRef.current?.querySelector('svg');
    if (!svg) {
      alert('QR code not found. Please try again.');
      return;
    }
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    // Set canvas size with padding
    const size = 300;
    const padding = 20;
    canvas.width = size + (padding * 2);
    canvas.height = size + (padding * 2) + 40; // Extra space for practice name
    
    img.onload = () => {
      // White background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw QR code
      ctx.drawImage(img, padding, padding, size, size);
      
      // Add practice name below QR code
      ctx.fillStyle = '#333333';
      ctx.font = 'bold 14px Arial, sans-serif';
      ctx.textAlign = 'center';
      const practiceName = practice.name || 'Practice';
      ctx.fillText(practiceName.substring(0, 35), canvas.width / 2, size + padding + 25);
      
      // Download
      const link = document.createElement('a');
      link.download = `smiledesign-qr-${practice.slug || 'practice'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  // Get the link to use for QR code - direct URL, no Bitly (Bitly shows preview page)
  const getQRLink = (practice) => {
    return `https://app.practcom.com/public-smile-design?practice=${practice.slug}`;
  };

  const filteredPractices = practices.filter(practice => {
    const matchesSearch = 
      practice.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      practice.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      practice.slug?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (statusFilter === 'all') return matchesSearch;
    if (statusFilter === 'active') {
      return matchesSearch && ['active', 'trial', 'beta'].includes(practice.status);
    }
    return matchesSearch && practice.status === statusFilter;
  });

  const practicesWithoutShortLinks = practices.filter(p => !p.shortLink).length;

  const getStatusBadge = (status) => {
    const variants = {
      'active': { variant: 'default', className: 'bg-green-600' },
      'trial': { variant: 'secondary', className: '' },
      'beta': { variant: 'default', className: 'bg-purple-600' },
      'inactive': { variant: 'destructive', className: '' },
      'cancelled': { variant: 'destructive', className: '' }
    };
    const config = variants[status] || { variant: 'outline', className: '' };
    return (
      <Badge variant={config.variant} className={config.className}>
        {status}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-600" />
            SmileDesign AI Links
          </h2>
          <p className="text-gray-600 mt-1">
            Shareable links for each practice - patients use these to access SmileDesign AI with practice branding
          </p>
        </div>
        <div className="flex gap-2">
          {practicesWithoutShortLinks > 0 && (
            <Button 
              onClick={generateAllShortLinks} 
              disabled={generatingAll}
              variant="outline"
              className="bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100"
            >
              <Zap className={`h-4 w-4 mr-2 ${generatingAll ? 'animate-pulse' : ''}`} />
              {generatingAll ? 'Generating...' : `Generate All Short Links (${practicesWithoutShortLinks})`}
            </Button>
          )}
          <Button onClick={loadPractices} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <Smartphone className="w-5 h-5 text-purple-600 mt-0.5" />
            <div>
              <p className="font-medium text-purple-900">SmileDesign AI Links:</p>
              <ul className="text-sm text-purple-800 mt-1 space-y-1">
                <li>• Each practice has a unique SmileDesign AI page</li>
                <li>• QR codes link directly to each practice's page</li>
                <li>• Consultation requests are automatically routed to the practice's email</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by name, email, or slug..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button 
                variant={statusFilter === 'active' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('active')}
              >
                Active ({practices.filter(p => ['active', 'trial', 'beta'].includes(p.status)).length})
              </Button>
              <Button 
                variant={statusFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setStatusFilter('all')}
              >
                All ({practices.length})
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-purple-600 border-t-transparent rounded-full mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading practices...</p>
            </div>
          ) : filteredPractices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No practices found matching your criteria
            </div>
          ) : (
            <div className="space-y-3">
              {/* Header Row */}
              <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-gray-100 rounded-lg font-medium text-sm text-gray-600">
                <div className="col-span-3">Practice Name</div>
                <div className="col-span-3">Email</div>
                <div className="col-span-3">SmileDesign Link</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-1 text-center">QR Code</div>
                <div className="col-span-1 text-right">Preview</div>
              </div>
              
              {/* Practice Rows */}
              {filteredPractices.map((practice) => (
                <div 
                  key={practice.id} 
                  className="grid grid-cols-12 gap-4 items-center p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="col-span-3">
                    <p className="font-semibold truncate" title={practice.name}>{practice.name}</p>
                    <p className="text-xs text-gray-500 truncate">Slug: {practice.slug}</p>
                  </div>
                  <div className="col-span-3">
                    <p className="text-sm text-gray-600 truncate" title={practice.email}>{practice.email}</p>
                  </div>
                  
                  {/* Link Column */}
                  <div className="col-span-3">
                    <div className="flex items-center gap-1">
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded truncate flex-1" title={`https://app.practcom.com/public-smile-design?practice=${practice.slug}`}>
                        ...?practice={practice.slug}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(`https://app.practcom.com/public-smile-design?practice=${practice.slug}`, practice.id, 'long')}
                        className={`h-7 w-7 p-0 ${copiedId === practice.id && copiedType === 'long' ? 'bg-green-50' : ''}`}
                      >
                        {copiedId === practice.id && copiedType === 'long' ? (
                          <Check className="w-3 h-3 text-green-600" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  <div className="col-span-1">
                    {getStatusBadge(practice.status)}
                  </div>
                  
                  {/* QR Code Button */}
                  <div className="col-span-1 flex justify-center">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setQrModalPractice(practice)}
                      className="bg-purple-50 border-purple-300 text-purple-700 hover:bg-purple-100 h-7 w-7 p-0"
                      title="View & Download QR Code"
                    >
                      <QrCode className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="col-span-1 flex justify-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => window.open(`${baseUrl}/public-smile-design?practice=${practice.slug}`, '_blank')}
                      title="Preview in new tab"
                      className="h-7 w-7 p-0"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-purple-600">{practices.length}</p>
              <p className="text-sm text-gray-600">Total Practices</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">
                {practices.filter(p => ['active', 'trial', 'beta'].includes(p.status)).length}
              </p>
              <p className="text-sm text-gray-600">Active Practices</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* QR Code Modal */}
      {qrModalPractice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setQrModalPractice(null)}>
          <div 
            className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">QR Code for {qrModalPractice.name}</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setQrModalPractice(null)}
                className="h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="flex flex-col items-center">
              <div ref={qrCodeRef} className="bg-white p-4 rounded-lg border-2 border-gray-200">
                <QRCode 
                  value={getQRLink(qrModalPractice)} 
                  size={200}
                  level="H"
                  bgColor="#FFFFFF"
                  fgColor="#000000"
                />
              </div>
              
              <p className="text-sm text-gray-600 mt-4 text-center">
                Scan to access SmileDesign AI
              </p>
              
              <div className="bg-gray-100 rounded-lg px-3 py-2 mt-2 w-full">
                <p className="text-xs text-gray-500 text-center truncate">
                  {getQRLink(qrModalPractice)}
                </p>
              </div>
              
              <div className="flex gap-3 mt-4 w-full">
                <Button
                  onClick={() => copyToClipboard(getQRLink(qrModalPractice), qrModalPractice.id, 'qr')}
                  variant="outline"
                  className="flex-1"
                >
                  {copiedId === qrModalPractice.id && copiedType === 'qr' ? (
                    <>
                      <Check className="w-4 h-4 mr-2 text-green-600" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Link
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => downloadQRCode(qrModalPractice)}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download PNG
                </Button>
              </div>
              
              <p className="text-xs text-gray-500 mt-4 text-center">
                Use this QR code on business cards, flyers, or waiting room displays.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
